// Tabular view of the property list. Toggled on via `?view=table` —
// see {@link PropertyViewToggle}. The card grid (`PropertyCard`) remains
// the default; this view is for owners who prefer dense scanning by
// row across many properties at once.
//
// Columns are tuned for desktop. On narrow screens we fall through to
// the grid via {@code md:hidden} on the wrapper; the page chooses which
// component to render at the breakpoint level.

import { useNavigate } from "react-router";

import { AuthImg } from "@/components/auth-img";
import { PropertyStatusBadge } from "@/features/property/components/property-status-badge";
import { PropertyFreshnessBadge } from "@/features/property/components/property-freshness-badge";
import { formatPrice } from "@/features/property/utils/format-price";
import { wardLabel } from "@/features/property/utils/ward-options";
import { cn } from "@/lib/utils";
import type { Property } from "@/types/property";

interface PropertyTableProps {
  items: readonly Property[];
  staleDays: number;
  className?: string;
}

export function PropertyTable({ items, staleDays, className }: PropertyTableProps) {
  const navigate = useNavigate();

  return (
    <div
      className={cn(
        "overflow-x-auto rounded-xl border border-border bg-card shadow-[var(--shadow-soft)]",
        className,
      )}
    >
      <table className="w-full border-collapse text-[14px]">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            <Th className="w-16">Ảnh</Th>
            <Th className="w-24">Mã</Th>
            <Th>Tòa nhà</Th>
            <Th>Khu vực</Th>
            <Th className="text-right">Diện tích</Th>
            <Th className="text-center">PN</Th>
            <Th className="text-center">WC</Th>
            <Th className="text-right">Giá</Th>
            <Th>Trạng thái</Th>
            <Th>Trạng thái cập nhật</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((property) => (
            <TableRow
              key={property.id}
              property={property}
              staleDays={staleDays}
              onSelect={() => navigate(`/properties/${property.id}`)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

function TableRow({
  property,
  staleDays,
  onSelect,
}: {
  property: Property;
  staleDays: number;
  onSelect: () => void;
}) {
  return (
    <tr
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      role="link"
      tabIndex={0}
      aria-label={`Xem chi tiết ${property.buildingName ?? property.internalCode ?? ""}`}
      className={cn(
        "cursor-pointer border-b border-border last:border-0 transition-colors",
        "hover:bg-muted/50 focus-visible:bg-muted focus-visible:outline-none",
      )}
    >
      <Td>
        <div className="aspect-[4/3] w-14 overflow-hidden rounded bg-muted">
          {property.thumbnailUrl ? (
            <AuthImg
              src={property.thumbnailUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : null}
        </div>
      </Td>
      <Td>
        <span className="font-mono text-[11px] text-ink-soft">
          {property.internalCode ?? "—"}
        </span>
      </Td>
      <Td>
        <div className="font-medium">{property.buildingName ?? "—"}</div>
        {property.floor !== null ? (
          <div className="text-[11px] text-muted-foreground">
            Tầng {property.floor}
          </div>
        ) : null}
      </Td>
      <Td>{wardLabel(property.wardCode) || "—"}</Td>
      <Td className="text-right font-mono tabular-nums">{property.area} m²</Td>
      <Td className="text-center font-mono tabular-nums">
        {property.bedrooms ?? "—"}
      </Td>
      <Td className="text-center font-mono tabular-nums">
        {property.bathrooms ?? "—"}
      </Td>
      <Td className="text-right font-mono font-semibold tabular-nums">
        {formatPrice(property.price, property.listingType)}
      </Td>
      <Td>
        <PropertyStatusBadge
          status={property.status}
          listingType={property.listingType}
        />
      </Td>
      <Td>
        <PropertyFreshnessBadge
          lastFreshnessConfirmedAt={property.lastFreshnessConfirmedAt}
          staleDays={staleDays}
        />
      </Td>
    </tr>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "px-3 py-2 text-left font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-faint",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={cn("px-3 py-2.5 align-middle", className)}>{children}</td>;
}
